import type { Money } from '@dastarhan/contracts';

/**
 * Скидки: карты, купоны и правила стола.
 *
 * Три источника скидки в одном заказе — обычное дело: у гостя карта,
 * стол «шеф» со своей скидкой, и кассир хочет добавить руками.
 * Складывать их нельзя: три раза по десять процентов превращаются
 * в треть счёта, чего никто не обещал. Берём наибольшую и называем
 * её вслух — гость должен понимать, за что ему уступили.
 */

export type CardKind = 'PERCENT' | 'COUPON';

export type DiscountCard = {
  id: string;
  code: string;
  kind: CardKind;
  percent: number;
  holder: string | null;
  isActive: boolean;
  usedAt: string | null;
  validUntil: string | null;
};

export type CardVerdict =
  | { ok: true; percent: number; label: string }
  | { ok: false; reason: string };

/**
 * Можно ли применить карту сейчас. Кассир вводит номер при госте,
 * и на каждый отказ у него должно быть объяснение вслух —
 * «карта не подошла» вызывает спор, «купон уже погашен вчера» нет.
 */
export function checkCard(card: DiscountCard | null, now: Date): CardVerdict {
  if (!card) return { ok: false, reason: 'Карта с таким номером не найдена' };
  if (!card.isActive) return { ok: false, reason: 'Карта отключена' };

  if (card.validUntil && new Date(card.validUntil) < now) {
    return { ok: false, reason: `Срок действия истёк ${fmt(card.validUntil)}` };
  }
  // Купон одноразовый: в этом вся разница между ним и картой
  if (card.kind === 'COUPON' && card.usedAt) {
    return { ok: false, reason: `Купон уже использован ${fmt(card.usedAt)}` };
  }
  if (card.percent <= 0) {
    return { ok: false, reason: 'У карты нулевая скидка' };
  }

  return {
    ok: true,
    percent: card.percent,
    label: card.kind === 'COUPON'
      ? `Купон ${card.code}`
      : `Карта ${card.holder ? card.holder : card.code}`,
  };
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU');
}

// ─────────────────────────────────────────── правила стола

export type PlaceRules = {
  /** процент сбора; null — брать общий по заведению, 0 — не брать */
  servicePct: number | null;
  /** скидка, положенная этому месту всегда; null — нет */
  discountPct: number | null;
};

/**
 * Правило для стола: своё важнее зального, зальное важнее общего.
 *
 * Так владелец задаёт «в баре сбора нет» одним движением по залу,
 * а не обходит каждую стойку. И тут же может выделить пять столов
 * «шеф» со своей скидкой, не трогая остальные.
 */
export function resolveRules(
  table: PlaceRules | null,
  hall: PlaceRules | null,
  accountServicePct: number,
): { servicePct: number; discountPct: number; source: 'table' | 'hall' | 'account' } {
  const svcFrom = table?.servicePct ?? null;
  const hallSvc = hall?.servicePct ?? null;

  const servicePct = svcFrom !== null ? svcFrom
    : hallSvc !== null ? hallSvc
      : accountServicePct;

  const discountPct = table?.discountPct ?? hall?.discountPct ?? 0;

  const source = svcFrom !== null || table?.discountPct != null ? 'table' as const
    : hallSvc !== null || hall?.discountPct != null ? 'hall' as const
      : 'account' as const;

  return { servicePct, discountPct, source };
}

// ─────────────────────────────────────────── сведение скидок

export type DiscountSource = { label: string; percent: number };

/**
 * Какая скидка достанется гостю, когда их несколько.
 *
 * Берём наибольшую, а не сумму: у гостя карта на 10%, стол «шеф»
 * даёт 10%, кассир добавил 5% за ожидание — сложить значит отдать
 * четверть счёта. Владелец такого не обещал ни в одном из трёх случаев.
 */
export function bestDiscount(sources: DiscountSource[]): DiscountSource | null {
  const live = sources.filter((s) => s.percent > 0);
  if (live.length === 0) return null;
  return live.reduce((best, s) => (s.percent > best.percent ? s : best));
}

/** Сумма скидки от подытога. Округление к ближайшему тиыну. */
export function discountAmount(subtotal: Money, percent: number): Money {
  if (percent <= 0 || subtotal <= 0) return 0;
  return Math.round((subtotal * Math.min(100, percent)) / 100);
}
